import React, { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDDatePicker from "components/MDDatePicker";
import MenuItem from "@mui/material/MenuItem";
import "flatpickr/dist/flatpickr.css";
import helpers from "../AnalyticsHelpers";

// estilo selects basados en MDInput
const selectSx = {
  "& .MuiInputBase-root": {
    height: 44, // misma altura que los MDInput por defecto
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    padding: "10.5px 14px", // igual que input outlined
  },
};

function AnalyticsSearch({
  apartmentOptions,
  onSubmit,
  individualApartmentId,
  individualApartmentName,
  metricsConfig,
  showCompareControls,
  compareSummaryText,
  onCompare,
  compareDisabled,
  loading,
  t,
}) {
  const isIndividual = !!individualApartmentId;

  const [apartmentSel, setApartmentSel] = useState("");
  const [mode, setMode] = useState("standart");
  const [measurement, setMeasurement] = useState("");
  const [period, setPeriod] = useState("");
  const [typeDataSelected, setTypeDataSelected] = useState("");

  const today = useMemo(() => new Date(), []);

  const [dateDaily, setDateDaily] = useState(helpers.toISODate(today));
  const [month, setMonth] = useState(
    `${today.getFullYear()}-${helpers.pad2(today.getMonth() + 1)}`
  );
  const [yearKey, setYearKey] = useState(today.getFullYear());
  const [startDate, setStartDate] = useState(helpers.toISODate(today));
  const [endDate, setEndDate] = useState(helpers.toISODate(today));

  const enabledMetrics = useMemo(() => helpers.getEnabledMetrics(metricsConfig), [metricsConfig]);

  const avaiblesOptionsInTypeData = useMemo(
    () => helpers.avaiblesOptionsInTypeDataBySelection(measurement, period, { isIndividual }),
    [measurement, period, isIndividual]
  );

  const selectedApt = useMemo(() => {
    if (isIndividual) {
      return {
        apartmentId: individualApartmentId,
        label: individualApartmentName,
        value: individualApartmentId,
      };
    }
    const one = apartmentOptions.find((a) => a?.value === apartmentSel);
    return one || null;
  }, [isIndividual, apartmentSel, apartmentOptions, individualApartmentId]);

  const isAllApartmentsSelected = selectedApt?.value === "ALL_APARTMENS";

  const periodOptions = useMemo(
    () => helpers.getPeriodOptions(isIndividual, mode),
    [isIndividual, mode]
  );

  // Mantener seleccionado un apartamento válido al cambiar entre modo individual y general(todos)
  useEffect(() => {
    if (isIndividual) {
      setApartmentSel(null);
    } else if (!apartmentSel || !apartmentOptions.find((a) => a?.value === apartmentSel)) {
      setApartmentSel(apartmentOptions[0]?.value || "");
    }
  }, [apartmentOptions, apartmentSel, isIndividual]);

  // Mantiene seleccionado un tipo de dato válido al cambiar la métrica o el periodo
  useEffect(() => {
    if (!measurement || avaiblesOptionsInTypeData.length === 0) {
      setTypeDataSelected("");
      return;
    }
    if (!typeDataSelected || !avaiblesOptionsInTypeData.find((a) => a?.key === typeDataSelected)) {
      setTypeDataSelected(avaiblesOptionsInTypeData[0]?.key);
    }
  }, [measurement, period, avaiblesOptionsInTypeData, typeDataSelected]);

  // Resetear periodo si no es válido al cambiar a byDates
  useEffect(() => {
    const valid = ["daily", "monthly"];
    if (mode === "byDates" && !valid.includes(period)) {
      setPeriod("");
    }
  }, [mode]);

  const handleSubmit = () => {
    if (!measurement || !period) return;
    if (mode === "byDates" && (!startDate || !endDate)) return;

    const fieldToSend =
      avaiblesOptionsInTypeData.length <= 1 ? avaiblesOptionsInTypeData[0]?.key : typeDataSelected;

    const referenceDate = helpers.getReferenceDate(mode, period, month, yearKey, dateDaily);

    const payload = isIndividual
      ? {
          allApartments: false,
          apartmentId: individualApartmentId,
          apartmentLabel: individualApartmentName,
          measurement,
          period,
          field: fieldToSend,
          mode,
          ...(mode === "byDates" ? { startDate, endDate } : { referenceDate }),
        }
      : {
          allApartments: isAllApartmentsSelected,
          deviceId: selectedApt?.onnaId,
          apartmentId: selectedApt?.apartmentId,
          apartmentLabel: selectedApt?.label,
          measurement,
          period,
          field: fieldToSend,
          mode,
          ...(mode === "byDates" ? { startDate, endDate } : { referenceDate }),
        };

    onSubmit?.(payload);
  };

  // FORM FIELDS
  return (
    <MDBox
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        mt: 2,
        mb: 2,
        alignItems: "end",
      }}
    >
      {/* Apartamento */}
      {!isIndividual && (
        <MDBox display="flex" flexDirection="column" gap={1}>
          <MDTypography variant="medium" fontWeight="regular" color="text">
            {t("apartment")}
          </MDTypography>

          <MDInput
            select
            fullWidth
            variant="outlined"
            value={apartmentSel}
            onChange={(e) => setApartmentSel(e.target.value)}
            SelectProps={{ MenuProps: { disableScrollLock: true } }}
            sx={selectSx}
          >
            {apartmentOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {t(opt.label)}
              </MenuItem>
            ))}
          </MDInput>
        </MDBox>
      )}

      {/* Modo */}
      <MDBox display="flex" flexDirection="column" gap={1}>
        <MDTypography variant="medium" fontWeight="regular" color="text">
          {t("mode")}
        </MDTypography>

        <MDInput
          select
          fullWidth
          variant="outlined"
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          SelectProps={{ MenuProps: { disableScrollLock: true } }}
          sx={selectSx}
        >
          <MenuItem value="standart">{t("standart")}</MenuItem>
          <MenuItem value="byDates">{t("byDates")}</MenuItem>
        </MDInput>
      </MDBox>

      {/* Métrica */}
      <MDBox display="flex" flexDirection="column" gap={1}>
        <MDTypography variant="medium" fontWeight="regular" color="text">
          {t("metric")}
        </MDTypography>

        <MDInput
          select
          fullWidth
          variant="outlined"
          value={measurement}
          onChange={(e) => setMeasurement(e.target.value)}
          SelectProps={{
            displayEmpty: true,
            renderValue: (selected) => {
              if (!selected) return `— ${t("select")} —`;
              const opt = enabledMetrics.find((m) => m.key === selected);
              return opt
                ? helpers.getMetricDisplayLabel({ metricKey: opt.key, metricsConfig, t })
                : selected;
            },
            MenuProps: { disableScrollLock: true },
          }}
          sx={selectSx}
        >
          <MenuItem value="">
            <em>— {`${t("select")}`} —</em>
          </MenuItem>
          {enabledMetrics
            .slice()
            .sort((a, b) =>
              helpers
                .getMetricDisplayLabel({ metricKey: a.key, metricsConfig, t })
                .localeCompare(
                  helpers.getMetricDisplayLabel({ metricKey: b.key, metricsConfig, t })
                )
            )
            .map((m) => (
              <MenuItem key={m.key} value={m.key}>
                {helpers.getMetricDisplayLabel({ metricKey: m.key, metricsConfig, t })}
              </MenuItem>
            ))}
        </MDInput>
      </MDBox>

      {/* Periodo */}
      <MDBox display="flex" flexDirection="column" gap={1}>
        <MDTypography variant="medium" fontWeight="regular" color="text">
          {t("period")}
        </MDTypography>

        <MDInput
          select
          fullWidth
          variant="outlined"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          SelectProps={{
            displayEmpty: true,
            renderValue: (selected) => {
              if (!selected) return `— ${t("select")} —`;

              const opt = periodOptions.find((p) => p?.key === selected);

              return t(opt?.label);
            },
            MenuProps: { disableScrollLock: true },
          }}
          sx={selectSx}
        >
          <MenuItem value="">
            <em>— {`${t("select")}`} —</em>
          </MenuItem>

          {periodOptions.map((opt) => (
            <MenuItem key={opt?.key} value={opt?.key}>
              {t(opt?.label)}
            </MenuItem>
          ))}
        </MDInput>
      </MDBox>

      {/* Fechas inicio / fin (byDates) */}
      {mode === "byDates" && (
        <MDBox display="flex" flexDirection="column" gap={1}>
          <MDTypography variant="medium" fontWeight="regular" color="text">
            {t("startDate")}
          </MDTypography>

          <MDDatePicker
            options={{
              dateFormat: "Y-m-d",
              allowInput: true,
            }}
            value={startDate}
            onChange={([date]) => {
              if (date) setStartDate(helpers.toISODate(date));
            }}
            onClose={(_, dateStr) => helpers.commitTypedISODate(dateStr, setStartDate)}
            input={{
              fullWidth: true,
              variant: "outlined",
              onBlur: (e) => helpers.commitTypedISODate(e.target.value, setStartDate),
            }}
          />
        </MDBox>
      )}

      {mode === "byDates" && (
        <MDBox display="flex" flexDirection="column" gap={1}>
          <MDTypography variant="medium" fontWeight="regular" color="text">
            {t("endDate")}
          </MDTypography>

          <MDDatePicker
            options={{
              dateFormat: "Y-m-d",
              allowInput: true,
            }}
            value={endDate}
            onChange={([date]) => {
              if (date) setEndDate(helpers.toISODate(date));
            }}
            onClose={(_, dateStr) => helpers.commitTypedISODate(dateStr, setEndDate)}
            input={{
              fullWidth: true,
              variant: "outlined",
              onBlur: (e) => helpers.commitTypedISODate(e.target.value, setEndDate),
            }}
          />
        </MDBox>
      )}

      {/* Día */}
      {(period === "hourly" || (isIndividual && period === "detail")) && (
        <MDBox display="flex" flexDirection="column" gap={1}>
          <MDTypography variant="medium" fontWeight="regular" color="text">
            {t("day")}
          </MDTypography>

          <MDDatePicker
            options={{
              dateFormat: "Y-m-d",
              allowInput: true,
            }}
            value={dateDaily}
            onChange={([date]) => {
              if (date) setDateDaily(helpers.toISODate(date));
            }}
            onClose={(_, dateStr) => helpers.commitTypedISODate(dateStr, setDateDaily)}
            input={{
              fullWidth: true,
              variant: "outlined",
              onBlur: (e) => helpers.commitTypedISODate(e.target.value, setDateDaily),
            }}
          />
        </MDBox>
      )}

      {/* Mes */}
      {period === "daily" && mode !== "byDates" && (
        <MDBox display="flex" flexDirection="column" gap={1}>
          <MDTypography variant="medium" fontWeight="regular" color="text">
            {t("month")}
          </MDTypography>

          <MDInput
            type="month"
            fullWidth
            variant="outlined"
            value={month}
            onChange={(e) => {
              const value = e?.target?.value || "";
              if (/^\d{4}-\d{2}$/.test(value)) {
                setMonth(value);
              }
            }}
            InputLabelProps={{ shrink: true }}
          />
        </MDBox>
      )}

      {/* Año */}
      {mode !== "byDates" && period === "monthly" && (
        <MDBox display="flex" flexDirection="column" gap={1}>
          <MDTypography variant="medium" fontWeight="regular" color="text">
            {t("year")}
          </MDTypography>

          <MDInput
            select
            fullWidth
            variant="outlined"
            value={yearKey}
            onChange={(e) => setYearKey(e.target.value)}
            SelectProps={{
              MenuProps: { disableScrollLock: true },
            }}
            sx={{
              "& .MuiInputBase-root": { height: 44 },
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                padding: "10.5px 14px",
              },
            }}
          >
            {Array.from(
              { length: new Date().getFullYear() - 2020 + 1 },
              (_, i) => new Date().getFullYear() - i
            ).map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </MDInput>
        </MDBox>
      )}

      {/* Tipo de dato */}
      {measurement && period && avaiblesOptionsInTypeData.length > 1 && (
        <MDBox display="flex" flexDirection="column" gap={1}>
          <MDTypography variant="medium" fontWeight="regular" color="text">
            {t("dataType")}
          </MDTypography>

          <MDInput
            select
            fullWidth
            variant="outlined"
            value={typeDataSelected}
            onChange={(e) => setTypeDataSelected(e.target.value)}
            SelectProps={{ MenuProps: { disableScrollLock: true } }}
            sx={selectSx}
          >
            {avaiblesOptionsInTypeData.map((opt) => (
              <MenuItem key={opt?.key} value={opt?.key}>
                {t(opt?.label)}
              </MenuItem>
            ))}
          </MDInput>
        </MDBox>
      )}

      {/* BOTONES DE ACCIÓN DE BUSQUEDA Y DE COMPARACIÓN */}
      <MDBox
        sx={{
          gridColumn: "1 / -1",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-end",
          gap: 1,
          flexWrap: "nowrap",
        }}
      >
        <MDButton
          color="info"
          variant="gradient"
          size="medium"
          disabled={loading}
          onClick={handleSubmit}
        >
          {t("makeQuery")}
        </MDButton>

        {showCompareControls && (
          <MDBox
            sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}
          >
            {!!compareSummaryText && (
              <MDTypography
                variant="button"
                fontWeight="medium"
                color="text"
                sx={{ whiteSpace: "nowrap" }}
              >
                {compareSummaryText}
              </MDTypography>
            )}
            <MDButton
              color="info"
              variant="gradient"
              size="medium"
              disabled={compareDisabled}
              onClick={onCompare}
            >
              {t("compareCharts")}
            </MDButton>
          </MDBox>
        )}
      </MDBox>
    </MDBox>
  );
}

AnalyticsSearch.propTypes = {
  apartmentOptions: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
  individualApartmentId: PropTypes.string,
  individualApartmentName: PropTypes.string,
  metricsConfig: PropTypes.object,
  showCompareControls: PropTypes.bool,
  compareSummaryText: PropTypes.string,
  onCompare: PropTypes.func,
  compareDisabled: PropTypes.bool,
  loading: PropTypes.bool,
  t: PropTypes.func,
};

AnalyticsSearch.defaultProps = {
  apartmentOptions: [],
  individualApartmentId: "",
  individualApartmentName: "",
  metricsConfig: {},
  showCompareControls: false,
  compareSummaryText: "",
  onCompare: () => {},
  compareDisabled: true,
  loading: false,
  t: () => {},
};

export default AnalyticsSearch;
